import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Grid, Stack, Tab, Tabs, IconButton } from "@mui/material";
import { Helmet } from "react-helmet";
import { useFlags } from "launchdarkly-react-client-sdk";

import PageLoader from "components/common/PageLoader";
import Overview from "components/Screens/Workforce/UserDetails/NewPersonalInfo";
import Contact from "components/Widgets/BambooHRTabs/Contact";
import SalaryDetails from "components/Widgets/BambooHRTabs/SalaryDetails";
import BankDetails from "components/Screens/Workforce/BankDetails/BankDetails";
import EmployeeEducation from "components/Widgets/BambooHRTabs/Education";
import EmployeeExperience from "components/Widgets/BambooHRTabs/Experience";
import EmployeeDocs from "components/Widgets/BambooHRTabs/Documents";
import Notes from "components/Screens/Workforce/UserDetailsV2/Notes";
import Dependents from "components/Screens/Workforce/UserDetailsV2/Dependents";
import EmployeeTimeOffDetails from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails";
import Timesheet from "components/Screens/Timesheet";
import BackIcon from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/Icons/BackIcon";
import EmployeeInfoTop from "components/common/EmployeeInfoTop";
import { checkUserAdditionalInfo, getFormattedDateWithDuration, getValue } from "components/Screens/CommonUtils";

import { employeeSalaryDetails } from "store/actions/Workforce/EmployeeDetils/SalaryDetails";
import { employeeSalaryInfo } from "store/actions/Workforce/EmployeeDetils/SalaryInfo";
import { GetEmployeeDetails, ResetGetEmployeeDetails } from "store/actions/GetEmployeeDetails";
import { getEmployeeDetailsByIDV2 } from "services/Admin/Dashboard";
import { getEmployeeStatus } from "services/BambooHrTabs/about";
import { ADMIN_WORKFORCE_TABS } from "constants";

import { baseUrl } from "util/APIBaseUrl";

import "./user-details.scss";

function Index() {
  const dispatch = useDispatch();
  const params = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const tab = urlParams.get("tab");
  const navigateFrom = urlParams.get("navigateFrom");
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const { checkinCheckout, bankDetailsAndDependants } = useFlags();

  const [currentTab, setCurrentTab] = useState("Overview");
  const [, setUserData] = useState({});
  const [tabs, setTabs] = useState(ADMIN_WORKFORCE_TABS);
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [timelineCurrentStatus, setTimelineCurrentStatus] = useState(null);

  const getEmployeeDetails = useSelector((userDetails) => userDetails.GetEmployeeDetails.getEmployeeDetails);
  const LOADING_GET_EMPLOYEE_DETAILS = useSelector(
    (userDetails) => userDetails.GetEmployeeDetails.loadingGET_EMPLOYEE_Details
  );
  const { userAdditionalInfo, loadingAdditionalInfo } = useSelector((userDetails) => userDetails.UserAdditionalInfo);
  const { loadingEmployeeSalaryInfo } = useSelector((userDetails) => userDetails.SalaryInfo);

  const {
    payload: {
      employeeId,
      firstName,
      lastName,
      middleName,
      jobTitle,
      timeoffFlag,
      customerId,
      pendingCustomerId,
      pictureVersion,
      edgeJoiningDate,
      startDate,
      startDateHR,
      employeeStatus,
      status,
    } = {},
  } = getEmployeeDetails;

  const isUnassigned = status === "unassigned";

  const { hrpoc } = employeeDetails;

  // Day-granular local-timezone comparison: hide the status chip only when the start date is
  // in the future. HR Start Date is the reference (falls back to DoJ — Employment_History.Joining_Date —
  // if HR start is null), matching the EmploymentInfo render-gate rule (startDateHR || joiningDate).
  const isScheduled = (() => {
    const value = startDateHR || employeeDetails?.customerInfo?.joiningDate;
    if (!value) return false;
    const target = new Date(value);
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return target > today;
  })();

  const handleBackButton = () => {
    if (navigateFrom) {
      return navigate(`/main/${navigateFrom}`);
    }
    return navigate("/main/workforce");
  };

  // This screen is reused across talents: the /main/details/:id route renders the same mounted
  // instance without remounting when navigating from one talent's profile to another's, so
  // params.id can change without React resetting employeeDetails on its own. currentEmployeeIdRef
  // always tracks the talent currently on screen so a response for a talent the admin has already
  // navigated away from (including a manual refetch that resolves late) is dropped instead of
  // overwriting the newer talent's data.
  const currentEmployeeIdRef = useRef(params.id);

  // TODO: Push this into redux as it is beind used by multiple components
  const fetchEmployeeDetailsByIDV2 = async () => {
    const requestedId = params.id;
    const res = await getEmployeeDetailsByIDV2(requestedId);
    if (currentEmployeeIdRef.current === requestedId) {
      setEmployeeDetails(res?.payload);
    }
  };

  useEffect(() => {
    currentEmployeeIdRef.current = params.id;
    setEmployeeDetails({});
    fetchEmployeeDetailsByIDV2();
  }, [params.id]);

  useEffect(() => {
    if (!employeeId || isScheduled || isUnassigned) {
      setTimelineCurrentStatus(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getEmployeeStatus(employeeId);
        if (!cancelled) {
          setTimelineCurrentStatus(response?.currentStatus || null);
        }
      } catch (error) {
        if (!cancelled) {
          setTimelineCurrentStatus(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, isScheduled, isUnassigned, customerId, pendingCustomerId]);

  useEffect(() => {
    let employeeSalaryDetailsRequest = "";
    let employeeSalaryInfoRequest = "";

    if (Object.keys(loggedInUser).length && loggedInUser.userId && getEmployeeDetails?.ok) {
      if (loggedInUser?.type == "customer") {
        employeeSalaryInfoRequest = `customer/${customerId}/employee/${employeeId}/salaryInfo`;
        employeeSalaryDetailsRequest = `customer/${customerId}/employee/${employeeId}/payroll`;
      } else if (loggedInUser?.type == "edge") {
        employeeSalaryInfoRequest = `employee/${employeeId}/salary`;
        employeeSalaryDetailsRequest = `employee/${employeeId}/payroll`;
      }

      const isClockInEnabledForEmployee = getEmployeeDetails?.payload?.isClockInAllowed === true;

      dispatch(
        employeeSalaryDetails({
          request: employeeSalaryDetailsRequest,
        })
      );

      dispatch(
        employeeSalaryInfo({
          request: employeeSalaryInfoRequest,
        })
      );

      const updatedTabs = [...ADMIN_WORKFORCE_TABS];

      if (bankDetailsAndDependants) {
        updatedTabs.push(
          {
            title: "Dependents",
            link: "",
          },
          {
            title: "Bank Details",
            link: "",
          }
        );
      }

      if (timeoffFlag && (customerId || pendingCustomerId)) {
        updatedTabs.push({ title: "Time-off", link: "" });
      }

      if (checkinCheckout && isClockInEnabledForEmployee) {
        updatedTabs.push({ title: "Timesheet", link: "" });
      }

      setTabs(updatedTabs);
    }
  }, [getEmployeeDetails]);

  useEffect(() => {
    setUserData(loggedInUser);

    if (params.id) {
      if (params.id !== employeeId) {
        dispatch(ResetGetEmployeeDetails({}));
      }
      if (
        (Object.keys(loggedInUser).length &&
          loggedInUser.userId &&
          !Object.keys(getEmployeeDetails).length &&
          params.id &&
          checkUserAdditionalInfo(userAdditionalInfo)) ||
        state?.redirectFrom === "editEmployee" ||
        params.id !== employeeId
      ) {
        if (loggedInUser?.type == "customer") {
          dispatch(
            GetEmployeeDetails({
              customerID: loggedInUser?.userId,
              employeeID: params.id,
              request: `customer/${userAdditionalInfo?.customerId}/employee/${params.id}`,
            })
          );
        } else if (loggedInUser?.type == "edge") {
          dispatch(
            GetEmployeeDetails({
              customerID: loggedInUser?.userId,
              employeeID: params.id,
              request: `employee/${params.id}`,
            })
          );
        }
      }
    }
  }, [params.id, state?.redirectFrom, employeeId, userAdditionalInfo]);

  const getTabFromHash = () => {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    return tabs.find((tabData) => tabData.title.toLowerCase() === hash.toLowerCase())?.title || "Overview";
  };

  useEffect(() => {
    if (tab) {
      setCurrentTab(tab);
    } else {
      const hashTab = getTabFromHash();
      setCurrentTab(hashTab);
    }

    const handleHashChange = () => {
      const hashTab = getTabFromHash();
      setCurrentTab(hashTab);
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [tab, tabs]);

  const handleChange = (event, newValue) => {
    window.location.hash = encodeURIComponent(newValue);
    setCurrentTab(newValue);
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case "Overview":
        return (
          <Overview
            fetchEmployeeDetailsByIDV2={fetchEmployeeDetailsByIDV2}
            employeeDetails={employeeDetails}
            employeeIDParams={params.id}
          />
        );
      case "Contact":
        return (
          <div className="admin-contact-wrapper">
            <Contact />
          </div>
        );
      case "Education":
        return <EmployeeEducation employeeId={employeeId} />;
      case "Salary Details":
        return (
          <SalaryDetails employeeDetails={employeeDetails} fetchEmployeeDetailsByIDV2={fetchEmployeeDetailsByIDV2} />
        );
      case "Bank Details":
        return <BankDetails employeeId={employeeId} />;
      case "Documents":
        return <EmployeeDocs />;
      case "Experience":
        return <EmployeeExperience employeeId={employeeId} />;
      case "Notes":
        return <Notes />;
      case "Dependents":
        return <Dependents />;
      case "Time-off":
        return <EmployeeTimeOffDetails />;
      case "Timesheet":
        return <Timesheet employeeId={employeeId} employeeDetails={employeeDetails} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Edge | Employees - ${`${getValue(firstName)} ${getValue(lastName)}`}`}</title>
      </Helmet>
      {LOADING_GET_EMPLOYEE_DETAILS || loadingAdditionalInfo || loadingEmployeeSalaryInfo ? <PageLoader /> : null}
      <div className="manage-timeoff-wrapper-admin">
        <Stack direction="row" alignItems="center" sx={{ mb: "12px", padding: "16px 32px" }}>
          <IconButton onClick={() => handleBackButton()}>
            <BackIcon />
          </IconButton>
          <EmployeeInfoTop
            img={`${baseUrl}/employee/${employeeId}/logo?id=${pictureVersion}&variant=thumbnail`}
            name={`${getValue(firstName, " ")} ${middleName ? getValue(middleName) : ""} ${getValue(lastName, " ")}`}
            jobTitle={getValue(jobTitle)}
            managedBy={`${getValue(hrpoc?.firstName)} ${getValue(hrpoc?.lastName, "")}`}
            startDate={isUnassigned ? null : getFormattedDateWithDuration((startDateHR || startDate)?.slice?.(0, 10))}
            employeeStatus={isUnassigned || isScheduled ? null : timelineCurrentStatus || employeeStatus}
            hiredDate={isUnassigned ? null : edgeJoiningDate}
            isScheduled={isScheduled}
          />
        </Stack>

        <div className="tabs-container">
          <Tabs
            value={currentTab}
            onChange={handleChange}
            sx={{
              ".MuiTabs-indicator": {
                height: "4px !important",
                zIndex: 2,
              },
              ".MuiTabs-flexContainer": {
                gap: "16px",
              },
              ".Mui-selected": {
                color: "#212231",
              },

              "& .MuiTabs-scroller": {
                paddingLeft: "16px",
              },
            }}
          >
            {tabs?.map((tabData) => (
              <Tab label={tabData?.title} value={tabData?.title} key={tabData} disableRipple className="holidays-tab" />
            ))}
          </Tabs>
          <span
            style={{
              display: "block",
              width: "100%",
              height: "1px",
              backgroundColor: "#B2B4E2",
              position: "absolute",
              bottom: "2px",
            }}
          />
        </div>
      </div>
      <Grid
        container
        direction="row"
        id="employeeInformation"
        className="custom-scroll"
        sx={{
          maxHeight: "calc(100vh - 260px)",
          overFlowY: "auto",
          paddingRight: "0px !important",
        }}
      >
        <div
          className={`w-full ${currentTab !== "Documents" && currentTab !== "Notes" && currentTab !== "Salary Details" && currentTab !== "Dependents" ? "pt-175 pb-15 px-2" : ""} bg-white`}
        >
          {renderTabContent()}
        </div>
      </Grid>
    </>
  );
}

export default Index;
